import classNames from 'classnames';
import React, {
  ChangeEvent,
  FC,
  memo,
  useCallback,
  useEffect,
  useState,
} from 'react';
import CherryIcon from '../../assets/icons/chery';
import EmailIcon from '../../assets/icons/email';
import Football from '../../assets/icons/football';
import Info from '../../assets/icons/Info';
import MailPost from '../../assets/icons/mailPost';
import Message from '../../assets/icons/message';
import Phone from '../../assets/icons/phone';
import WarningIcon from '../../assets/icons/warningIcon';
import { MultiSelect } from '../../design-system/MultiSelect';
import { useRegistrationStore } from '../../store/store';
import { TMultiMarketingConsentField } from '../../types/types';

type MultiMarketingConsentProps = TMultiMarketingConsentField & {
  onChange: (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onClear: () => void;
  value: string;
  onBlur?: (field: Partial<TMultiMarketingConsentField>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
};

interface ImageProps {
  className?: string;
  width?: string;
  onClick?: () => void;
  height?: string;
  src: string;
  title?: string;
  alt?: string;
  style?: object;
}

export const Image: React.FC<ImageProps> = ({
  className,
  width,
  onClick,
  height,
  src,
  title,
  alt,
  style,
}) => {
  const imgClass = classNames('object-cover', className);

  return (
    <img
      width={width || 'auto'}
      height={height || 'auto'}
      src={src}
      title={title}
      alt={alt}
      className={imgClass}
      onClick={onClick}
      style={style}
    />
  );
};

const arraysEqual = (a: string[] = [], b: string[] = []) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

const MultiMarketingConsentInner: FC<MultiMarketingConsentProps> = ({
  smsLabel,
  title,
  casinoLabel,
  sportsLabel,
  emailLabel,
  phoneLabel,
  postLabel,
  verticalError,
  verticalWarning,
  contactError,
  contactWarning,
  verticalTitle,
  contactTitle,
  verticalSubtitle,
}) => {
  const { setMetadata, metadata } = useRegistrationStore();

  const [verticals, setVerticals] = useState<string[]>(
    metadata?.marketingVerticals || [],
  );
  const [contacts, setContacts] = useState<string[]>(
    metadata?.marketingContacts || [],
  );

  const [highlightVerticals, setHighlightVerticals] = useState(false);
  const [highlightContacts, setHighlightContacts] = useState(false);

  useEffect(() => {
    const storedVerticals = metadata?.marketingVerticals || [];
    const storedContacts = metadata?.marketingContacts || [];

    if (!arraysEqual(storedVerticals, verticals)) {
      setVerticals(storedVerticals);
    }

    if (!arraysEqual(storedContacts, contacts)) {
      setContacts(storedContacts);
    }

  }, [metadata]);
  
  const persistToMetadata = useCallback(
    (selectedVerticals: string[], selectedContacts: string[]) => {
      setHighlightVerticals(false);
      setHighlightContacts(false);

      const draft = {
        casino: {
          sms:   selectedVerticals.includes('casino') && selectedContacts.includes('sms'),
          email: selectedVerticals.includes('casino') && selectedContacts.includes('email'),
          phone: selectedVerticals.includes('casino') && selectedContacts.includes('phone'),
          post:  selectedVerticals.includes('casino') && selectedContacts.includes('post'),
        },
        sports: {
          sms:   selectedVerticals.includes('sports') && selectedContacts.includes('sms'),
          email: selectedVerticals.includes('sports') && selectedContacts.includes('email'),
          phone: selectedVerticals.includes('sports') && selectedContacts.includes('phone'),
          post:  selectedVerticals.includes('sports') && selectedContacts.includes('post'),
        },
      };

      const needsContacts = selectedVerticals.length > 0 && selectedContacts.length === 0;
      const needsVerticals = selectedVerticals.length === 0 && selectedContacts.length > 0;

      setMetadata({
        marketingConsentDraft: draft,
        marketingVerticals:    selectedVerticals,
        marketingContacts:     selectedContacts,
        marketingHighlights:   {
          contacts:  needsContacts,
          verticals: needsVerticals,
        },
      });
    },
    [setMetadata],
  );

  const onVerticalChange = useCallback(
    (selected: string[]) => {
      setVerticals(selected);
      persistToMetadata(selected, contacts);
    },
    [persistToMetadata, contacts],
  );

  const onContactChange = useCallback(
    (selected: string[]) => {
      setContacts(selected);
      persistToMetadata(verticals, selected);
    },
    [persistToMetadata, verticals],
  );

  const validationTrigger = (
    metadata as unknown as { marketingValidationTrigger?: number }
  )?.marketingValidationTrigger;

  const requestedHighlights = (
    metadata as unknown as {
      marketingHighlights?: { contacts?: boolean; verticals?: boolean };
    }
  )?.marketingHighlights;

  useEffect(() => {
    if (validationTrigger) {
      setHighlightContacts(Boolean(requestedHighlights?.contacts));
      setHighlightVerticals(Boolean(requestedHighlights?.verticals));
    }
  }, [validationTrigger, requestedHighlights]);

  return (
    <div className="bg-white">
      {verticalSubtitle && (
        <div className="text-xs text-grey-70 mb-3xs">{verticalSubtitle}</div>
      )}

      <MultiSelect
        id="vertical"
        title={verticalTitle || 'I want goodies on'}
        onSelectionChange={onVerticalChange}
        columns={2}
        value={verticals}
        dataTestId='multi-select-vertical-title'
      >
        <MultiSelect.Item
          highlight={highlightVerticals}
          icon={<CherryIcon className="w-md h-md mr-2xs" />}
          label={casinoLabel || 'Casino'}
          value="casino"
        />
        <MultiSelect.Item
          highlight={highlightVerticals}
          icon={<Football className="w-md h-md mr-2xs" />}
          label={sportsLabel || 'Sports'}
          value="sports"
        />
      </MultiSelect>

      {verticals.length === 0 && contacts.length > 0 && !highlightVerticals && (
        <div className="flex items-center pt-2xs">
          <Info className="w-md h-md mr-2xs" />
          <div className="text-xs text-grey-70">
            {verticalWarning || 'Pick at least one so we know what to send.'}
          </div>
        </div>
      )}

      {verticals.length === 0 && contacts.length > 0 && highlightVerticals && (
        <div className="flex items-center pt-2xs">
          <WarningIcon className="w-md h-md mr-2xs" />
          <div className="text-xs text-grey-70">
            {verticalError || 'Oops! Tick one to carry on.'}
          </div>
        </div>
      )}

      <MultiSelect
        id="contact-type"
        title={contactTitle || title || 'Send me goodies by'}
        onSelectionChange={onContactChange}
        columns={2}
        value={contacts}
        dataTestId='multi-select-contact-type-title'
      >
        <MultiSelect.Item
          highlight={highlightContacts}
          icon={<EmailIcon className="w-md h-md mr-2xs" />}
          label={emailLabel || 'Email'}
          value="email"
        />
        <MultiSelect.Item
          highlight={highlightContacts}
          icon={<Message className="w-md h-md mr-2xs" />}
          label={smsLabel || 'SMS'}
          value="sms"
        />
        <MultiSelect.Item
          highlight={highlightContacts}
          icon={<Phone className="w-md h-md mr-2xs" />}
          label={phoneLabel || 'Phone'}
          value="phone"
        />
        <MultiSelect.Item
          highlight={highlightContacts}
          icon={<MailPost className="w-md h-md mr-2xs" />}
          label={postLabel || 'Post'}
          value="post"
        />
      </MultiSelect>

      {verticals.length > 0 && contacts.length === 0 && !highlightContacts && (
        <div className="flex items-center pt-2xs">
          <Info className="w-md h-md mr-2xs" />
          <div className="text-xs text-grey-70">
            {contactWarning || 'Tap a contact option to get perks.'}
          </div>
        </div>
      )}

      {verticals.length > 0 && contacts.length === 0 && highlightContacts && (
        <div className="flex items-center pt-2xs">
          <WarningIcon className="w-md h-md mr-2xs" />
          <div className="text-xs text-grey-70">
            {contactError || 'Oops! Tick one to carry on.'}
          </div>
        </div>
      )}
    </div>
  );
};

const MultiMarketingConsent = memo(MultiMarketingConsentInner);
MultiMarketingConsent.displayName = 'MultiMarketingConsent';

export { MultiMarketingConsent };
export type { MultiMarketingConsentProps };
